import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MergeTaskTableComponent } from './merge-task-table/merge-task-table.component';
import { ITasksMap, TaskInfoResp } from 'src/app/shared/models/tasks-planned';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';
import { TaskInfoService } from 'src/app/shared/services/task-info/task-info.service';
import { TaskInfoFormatterService } from 'src/app/shared/services/task-info/task-info-formatter.service';
import { take, Subscription, interval, mergeMap, takeWhile } from 'rxjs';
import { TaskType } from 'src/app/shared/models/core';
import { AppConstants, IntervalDuration } from 'src/app/shared/entity/constants';
import { ConnectionService } from 'src/app/shared/services/connection/connection.service';

import { CommandType } from 'src/app/shared/entity/enums';
import { TaskMovementService } from 'src/app/shared/services/operations/task-movement.service';
import { MessageService } from 'primeng/api';
import { HttpStatusCode } from '@angular/common/http';
import { ErrorMessage, ErrorType } from 'src/app/shared/entity/error.const';

export enum MergeTaskStep {
  SelectTarget = 0,
  SelectTask = 1,
  Preview = 2,
  Result = 3
}

interface MergeTaskCommandPayload {
  id: string;
  command_type: string;
  route_id: string;
  timestamp: Date;
  user_id: string;
  bl_id: string;
  target_task_id: string;
  source_task_ids: string[];
}

@Component({
    selector: 'app-merge-task-dialog',
    standalone: true,
    imports: [CommonModule, MergeTaskTableComponent],
    templateUrl: './merge-task-dialog.component.html',
    styleUrl: './merge-task-dialog.component.scss'
})
export class MergeTaskDialogComponent implements OnInit, OnDestroy {
  private dialogOpen = false;
  private plannedDeltaSubscription: Subscription | null = null;
  private shouldRefreshOnClose = false;
  private mergeTaskCollection: Map<string, ITasksMap> = new Map<string, ITasksMap>();
  @ViewChild('mergeTaskTable') mergeTaskTable: any;
  @Input() routeDetails: any;
  @Output() closeDialog = new EventEmitter<void>();

  currentStep = MergeTaskStep.SelectTarget;
  MergeTaskStep = MergeTaskStep;

  isSuccess: boolean | null = null;
  errorMessage = '';
  successMessage = '';
  warningMessage: string | null = null;

  routeTasks: ITasksMap[] = [];
  targetTaskSelection: ITasksMap[] = [];
  selectedTasks: ITasksMap[] = [];
  targetTask: ITasksMap | null = null;
  loading = false;
  steps = [
    { label: 'Select Target Task', subtitle: 'Choose which task will receive the merged items', step: MergeTaskStep.SelectTarget },
    { label: 'Select Tasks for Merge', subtitle: 'Choose which tasks you want to merge', step: MergeTaskStep.SelectTask },
    { label: 'Preview & Confirm', subtitle: 'Review changes and confirm', step: MergeTaskStep.Preview }
  ];

  constructor(
    private readonly dataRepositoryService: DataRepositoryService,
    private readonly taskInfoService: TaskInfoService,
    private readonly taskInfoFormatterService: TaskInfoFormatterService,
    private readonly cdr: ChangeDetectorRef,
    private readonly connectionService: ConnectionService,
    private readonly taskMovementService: TaskMovementService,
    private readonly messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.dialogOpen = true;

    this.loadRouteTasks();
    if (this.routeDetails?.route_id) {
      this.plannedTaskDelta();
    }
  }

  /**
   * Sets up a subscription to receive delta updates for planned tasks. 
   * It uses an interval to periodically fetch the latest delta updates for the current route.
   */
  plannedTaskDelta(): void {
    this.plannedDeltaSubscription = interval(IntervalDuration.TASKDELTA)
    .pipe(
          takeWhile(() => this.dialogOpen),
          mergeMap(() => this.taskInfoService.getPlannedBulk(
            [this.routeDetails.route_id],
            this.dataRepositoryService.dateOffset,
            true
          ))
    )
    .subscribe({
      next: (response: TaskInfoResp) => {
        const mappedDeltaTasks = this.taskInfoFormatterService.taskDeltaAdaptArray(response);
        this.updateMergeTaskCollection(mappedDeltaTasks, response.removed_tasks || []);
        this.dataRepositoryService.plannedTbEtag = response.etag ?? this.dataRepositoryService.plannedTbEtag;
        this.reconcileSelectionStateFromDelta();
        this.cdr.detectChanges();
      }
      });
  }

  

  /**
   * Updates the local collection of tasks with the latest delta updates. 
   * It adds new tasks, updates existing tasks, and removes deleted tasks from the collection.
   */
  private reconcileSelectionStateFromDelta(): void {
    const tasks = this.getTasksFromCollection();
    const currentTaskIds = new Set(tasks.map(task => task.task_id));

    this.targetTaskSelection = [...tasks];

    let warningMessage: string | null = null;
    let targetWasRemoved = false;

    if (this.targetTask && !currentTaskIds.has(this.targetTask.task_id)) {
      targetWasRemoved = true;
      this.targetTask = null;
      this.selectedTasks = [];
      warningMessage = 'Selected target task was removed. Please select another target task.';
      if (this.currentStep !== MergeTaskStep.Result) {
        this.currentStep = MergeTaskStep.SelectTarget;
      }
    }

    if (targetWasRemoved) {
      // Refresh target selection table with current valid tasks after target removal.
      this.targetTaskSelection = [...tasks];
    }

    this.routeTasks = this.targetTask
      ? tasks.filter(task => task.task_id !== this.targetTask?.task_id)
      : [...tasks];

    if (!warningMessage && this.selectedTasks.length) {
      const previousSelectedCount = this.selectedTasks.length;
      const filteredSelectedTasks = this.selectedTasks.filter(task => currentTaskIds.has(task.task_id));
      const removedCount = previousSelectedCount - filteredSelectedTasks.length;

      if (removedCount > 0) {
        this.selectedTasks = filteredSelectedTasks;

        if (filteredSelectedTasks.length === 0) {
          warningMessage = 'All selected source tasks were removed. Please select source tasks again.';
          if (this.currentStep !== MergeTaskStep.Result) {
            this.currentStep = MergeTaskStep.SelectTask;
          }
        } else {
          warningMessage = 'Some selected source tasks were removed. Please review your source selection.';
        }
      }
    }

    const isPreviewStep = this.currentStep === MergeTaskStep.Preview;
    if (!warningMessage && isPreviewStep && this.isFinalStepDisabled) {
      warningMessage = !this.targetTask
        ? 'Selected target task was removed. Please select another target task.'
        : 'All selected source tasks were removed. Please select source tasks again.';
    }

    if (!warningMessage && this.warningMessage && this.currentStep !== MergeTaskStep.Result) {
      this.warningMessage = null;
      return;
    }

    this.warningMessage = warningMessage;
  }

  // Disable the final action (Preview -> Confirm) when required merge prerequisites are no longer valid.
  get isFinalStepDisabled(): boolean {
    return !this.targetTask || !this.selectedTasks.length;
  }

  ngOnDestroy(): void {
    this.dialogOpen = false;

    if (this.plannedDeltaSubscription) {
      this.plannedDeltaSubscription.unsubscribe();
    }
    this.mergeTaskCollection.clear();
    this.selectedTasks = [];
    this.targetTask = null;
    this.routeTasks = [];
    this.errorMessage = '';
    this.isSuccess = null;
    this.currentStep = MergeTaskStep.SelectTask;
  }

  /**
   * Method to load tasks for the given route. It first checks if the tasks are already present in the local collection and uses them if available.
   * If not, it makes an API call to fetch the tasks for the route, formats them, and updates the local state. It also handles loading state and errors.
   * @returns void
   */
  private loadRouteTasks(): void {
    if (!this.routeDetails?.route_id) return;
    const existing = this.getTasksFromCollection();
    if (existing.length > 0) {
      this.routeTasks = existing;
      return;
    }
    this.loading = true;
    this.taskInfoService.getPlannedBulk(
      [this.routeDetails.route_id],
      this.dataRepositoryService.dateOffset,
      false
    ).pipe(take(1)).subscribe({
      next: (response: TaskInfoResp) => {
        const mappedTasks = this.taskInfoFormatterService.taskDeltaAdaptArray(response);
        this.updateMergeTaskCollection(mappedTasks, response.removed_tasks || []);
        this.routeTasks = this.getTasksFromCollection();
        this.targetTaskSelection = this.routeTasks;
        this.dataRepositoryService.plannedTbEtag = response.etag ?? this.dataRepositoryService.plannedTbEtag;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
      complete: () => {
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Retrieves the tasks from the local collection for the current route.
   * It filters the tasks based on the route ID and task type (Deliver) to return only the relevant tasks for merging.
   * @returns An array of tasks that match the current route ID and are of type Deliver.
   */
  private getTasksFromCollection(): ITasksMap[] {
    const tasks = this.mergeTaskCollection;
    if (!tasks) {
      return [];
    }
    return [...tasks.values()]
      .filter((t: ITasksMap) => (t.route_id === this.routeDetails.route_id && t.task_type.toLowerCase() === TaskType.Deliver))
      .sort((a, b) => {
        const stopDiff = (a.stop_sequence ?? 0) - (b.stop_sequence ?? 0);
        if (stopDiff !== 0) return stopDiff;

        return (a.task_sequence ?? 0) - (b.task_sequence ?? 0);
      });
  }

  /**
   * Method to handle the selection of tasks for merging. 
   * It updates the local state with the selected tasks and resets the target task if any of the selected tasks are removed in the delta update.
   * @param tasks The tasks to be selected for merging.
   */
  onTasksSelected(tasks: ITasksMap[]): void {
    if (Array.isArray(tasks)) {
      this.selectedTasks = tasks;
    } else if (tasks) {
      this.selectedTasks = [tasks];
    } else {
      this.selectedTasks = [];
    }
  }

  /**
   * This method is called when a target task is selected for merging. 
   * It updates the local state with the selected target task.
   * And removes it from the route tasks to prevent it from being selected as a source task in the next step.
   * @param task  The task to be set as the target for merging.
   */
  onTargetTaskSelected(task: any): void {
    this.targetTask = task ?? null;
    const tasks = this.getTasksFromCollection();
    this.routeTasks = this.targetTask
      ? tasks.filter(routeTask => routeTask.task_id !== this.targetTask?.task_id)
      : tasks;
    this.selectedTasks = [];
  }

  /**
   * Method to navigate to the next step in the merge task process. 
   * It checks if the current step is less than the Result step before incrementing the step.
   */
  next(): void {
    if (this.currentStep < MergeTaskStep.Result) {
      this.currentStep++;
    }
  }

  /**
   * Method to navigate back to the previous step in the merge task process. 
   * It checks if the current step is greater than the SelectTarget step before decrementing the step.
   */
  back(): void {
    if (this.currentStep > MergeTaskStep.SelectTarget) {
      this.currentStep--;
    }
  }

  /**
   * Cancels the merge task operation and closes the dialog. 
   * If there were changes that require a refresh of tasks and route information, 
   * it triggers the refresh before closing.
   */
  cancel(): void {
    if (this.shouldRefreshOnClose) {
      this.refreshTasksAndRouteInfo();
      this.shouldRefreshOnClose = false;
    }
    this.closeDialog.emit();
  }

  dismissWarning(): void {
    this.warningMessage = null;
  }

  /**
   * Method to confirm the merge task operation. It builds the payload for the merge command and makes an API call to execute the merge.
   * It handles the response to determine if the merge was successful, if there were any errors, and updates the local state accordingly.
   * Provides feedback to the user based on the result of the merge operation and updates the UI to reflect the changes.
   * @returns 
   */
  confirm(): void {
    if (!this.targetTask || !this.selectedTasks.length) {
      this.isSuccess = false;
      this.currentStep = MergeTaskStep.Result;
      return;
    }
    const payload = this.buildMergeTaskPayload();
    this.loading = true;
    this.connectionService.httpReq({
      reqType: AppConstants.POSTREQUEST,
      endUrl: AppConstants.Command,
      directoryType: AppConstants.DP,
      data: payload,
      responseType: 'text'
    }).pipe(take(1)).subscribe({
      next: (response) => {
        const resp = response ? JSON.parse(response)[0] : {};
        if(resp?.command_result?.status === 'FAILED') {
          this.errorMessage = resp?.command_result?.message;
          this.isSuccess = false;
        }
        if(resp?.command_result?.status === 'SUCCESS') {
          this.isSuccess = true;
          this.messageService.add({
            severity: ErrorType.SUCCESS,
            closable: false,
            summary: "",
            detail: resp?.command_result?.message ?? ErrorMessage.MERGE_SUCCESS,
          });
          this.closeDialog.emit();
          this.refreshTasksAndRouteInfo();
        }
        if(resp?.command_result?.status === 'PARTIAL_SUCCESS') {
          this.isSuccess = true;
          this.successMessage = resp?.command_result?.message;
          this.warningMessage = resp?.command_result?.details?.skipped_tasks;
        }
        this.shouldRefreshOnClose = true;
        this.currentStep = MergeTaskStep.Result;
        this.loading = false;
        this.plannedDeltaSubscription?.unsubscribe();
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        if(error?.status && error?.status === HttpStatusCode.Forbidden){
          this.errorMessage = ErrorMessage.RESTRICTIONMESSAGE;
        }
        this.isSuccess = false;
        this.currentStep = MergeTaskStep.Result;
        this.loading = false;
        this.plannedDeltaSubscription?.unsubscribe();
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Refreshes the tasks and route information by fetching the latest data from the backend.
   * It updates the local state with the new tasks and route details.
   * @returns void
   */
  private refreshTasksAndRouteInfo(): void {
    const routeId = this.routeDetails?.route_id;
    if (!routeId) {
      return;
    }
    this.taskMovementService.updateWithDeltaPostTaskMovement();
  }

  /**
   * Method to build the payload for the merge task command.
   * It constructs an array of MergeTaskCommandPayload objects based on the selected tasks and target task. 
   * The payload includes details such as route ID, user ID, business location ID, target task ID, and source task IDs.
   * @returns An array of MergeTaskCommandPayload objects.
   */
  private buildMergeTaskPayload(): MergeTaskCommandPayload[] {
    const blId = this.targetTask?.bl_id
      || this.targetTask?.business_location_id
      || this.routeDetails?.bl_id
      || this.routeDetails?.business_location_id
      || '';

    return [{
      id: crypto.randomUUID(),
      command_type: CommandType.MergeTaskCommand,
      route_id: this.routeDetails?.route_id ?? '',
      timestamp: new Date(),
      user_id: this.dataRepositoryService?.loggedInUser?.username ?? '',
      bl_id: blId,
      target_task_id: this.targetTask?.task_id ?? '',
      source_task_ids: this.selectedTasks.map(task => task.task_id)
    }];
  }

  /**
   * Preview step: target task, then all selected tasks to be merged.
   */
  get previewTableRows(): ITasksMap[] {
    const rows: ITasksMap[] = [];
    if (this.targetTask) {
      rows.push(this.targetTask);
    }
    rows.push(...this.selectedTasks);
    return rows;
  }

  /**
   * Returns true if any selected source task has a specific time window
    * (i.e. its task_time_window differs from default all-day window).
   * Warning is shown for cases:
   *  - Task B (specific) merged to Task A (default)
   *  - Task B (specific) merged to Task A (specific)
   */
  get hasTimeWindowWarning(): boolean {
    return this.selectedTasks.some(task => this.isSourceTaskSpecific(task));
  }

  /**
   * Generates a warning message listing which source tasks have a specific time window.
   */
  get timeWindowWarningMessage(): string {
    const specificTasks = this.selectedTasks.filter(task => this.isSourceTaskSpecific(task));
    if (!specificTasks.length) {
      return '';
    }

    const taskNames = specificTasks
      .map(task => task.task_point_name || task.task_id)
      .filter(Boolean);

    if (taskNames.length === 1) {
      return `${taskNames[0]} has a Specific Delivery window that conflicts with the target's window. This constraint cannot be merged and will be removed.`;
    }

    return `${taskNames.join(', ')} have Specific Delivery windows that conflict with the target's window. These constraints cannot be merged and will be removed.`;
  }

  /**
   * Returns true if the source task has a specific (non-default) time window
   * and it differs from target task's time window.
   */
  private isSourceTaskSpecific(task: ITasksMap): boolean {
    const sourceWindow = this.toWindowKey(task?.task_time_window);
    if (!sourceWindow) {
      return false;
    }

    if (this.isDefaultTimeWindow(sourceWindow)) {
      return false;
    }

    const targetWindow = this.toWindowKey(this.targetTask?.task_time_window);
    return !targetWindow || sourceWindow !== targetWindow;
  }

  private isDefaultTimeWindow(windowKey: string): boolean {
    return windowKey === '00:00-23:59';
  }

  /**
   * Normalizes time windows for comparison using 24-hour format only.
   */
  private toWindowKey(value: unknown): string {
    const range = this.extractTimeRange(value);
    if (!range) {
      return '';
    }

    return `${range.start}-${range.end}`;
  }

  /**
   * Extracts time range from a string or object and formats it to 24-hour format.
   */

  private extractTimeRange(value: unknown): { start: string; end: string } | null {
    const toTime = (time?: string): string => {
      const match = /(\d{1,2}):(\d{2})/.exec((time ?? '').trim());
      if (!match) {
        return '';
      }
      return `${match[1].padStart(2, '0')}:${match[2]}`;
    };

    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'object') {
      const obj = value as { begin?: string; end?: string };
      const start = toTime(obj.begin);
      const end = toTime(obj.end);
      return start && end ? { start, end } : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const timePattern = /\d{1,2}:\d{2}(?::\d{2})?/g;
    const matches = value.match(timePattern) || [];
    if (matches.length < 2) {
      return null;
    }

    const start = toTime(matches[0]);
    const end = toTime(matches.at(-1));
    return start && end ? { start, end } : null;
  }

  /**
   *  This method is used to call task delta when merge task pop up is open
   */
  private updateMergeTaskCollection(TaskMapObj: Map<string, ITasksMap>, removedTasks: string[]): void {
    if (TaskMapObj?.size) {
      TaskMapObj.forEach((task: ITasksMap) => {
        this.mergeTaskCollection.set(task.task_id, task);
      });
    }

    if (removedTasks?.length) {
      removedTasks.forEach((taskId: string) => this.mergeTaskCollection.delete(taskId));
    }

    this.mergeTaskCollection = new Map(this.mergeTaskCollection.entries());
  }
}
